import { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  CreditCard,
  Eye,
  EyeOff,
  Lock,
  Mail,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import DoorBrandLockup from "@/components/DoorBrandLockup";
import { useOrgDoor, type DoorOrg } from "@/hooks/useOrgDoor";
import { CaptchaField, useCaptcha } from "@/components/Captcha";
import { api } from "@/lib/api";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { safeRedirectPath } from "@/lib/roleHome";
import { PASSWORD_MIN_LENGTH, firstPasswordIssue } from "@/lib/password";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useAuth, WrongOrgDoorError } from "@/contexts/AuthContext";

const REMEMBER_KEY = "admin_remembered_email";

const ADMIN_FEATURES = [
  {
    icon: BarChart3,
    title: "Marketplace Management",
    desc: "Monitor listings, users, and transactions in real time.",
  },
  {
    icon: Users,
    title: "User & Role Administration",
    desc: "Control access with secure role-based permissions.",
  },
  {
    icon: CreditCard,
    title: "Subscription & Payment Control",
    desc: "Manage subscriptions, payments, and support requests.",
  },
  {
    icon: ShieldCheck,
    title: "Enterprise-Grade Security",
    desc: "Track platform activity with complete visibility.",
  },
];

// Compact brand lockup shown above the form on phones/tablets, where the full
// decorative BrandAside is hidden (< lg).
function MobileBrandHeader({ org }: { org: DoorOrg | null }) {
  return (
    <div className="lg:hidden">
      <DoorBrandLockup org={org} className="h-10 sm:h-12" orgClassName="h-12 sm:h-14" />
      <h1 className="mt-4 font-display text-2xl font-bold leading-[1.15] text-foreground sm:text-3xl">
        Take complete <span className="text-primary">control</span> of your <span className="text-primary">marketplace</span>.
      </h1>
      <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
        Manage listings, users, subscriptions, payments, and platform operations from one secure administrative workspace.
      </p>
    </div>
  );
}

function BrandAside({ org }: { org: DoorOrg | null }) {
  return (
    <aside className="public-auth-aside relative hidden overflow-hidden lg:flex lg:min-h-screen lg:flex-col lg:border-r lg:border-border/50">
      <div className="relative z-10 flex h-full flex-col px-10 pb-12 pt-16 xl:px-14">
        <DoorBrandLockup org={org} className="h-14" orgClassName="h-14" />

        <h1 className="mt-9 font-display text-4xl font-bold leading-[1.08] text-foreground xl:text-[2.7rem]">
          Take complete <span className="text-primary">control</span>
          <br />
          of your <span className="text-primary">marketplace</span>.
        </h1>

        <p className="mt-5 max-w-md text-[0.95rem] leading-7 text-muted-foreground">
          Manage listings, users, subscriptions, payments, and platform operations from one secure administrative workspace.
        </p>

        <div className="mt-9 space-y-4">
          {ADMIN_FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <div className="text-sm font-semibold text-foreground">{title}</div>
                <div className="mt-0.5 text-xs leading-6 text-muted-foreground">{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <div className="relative flex items-center gap-4 overflow-hidden rounded-2xl border border-primary/20 bg-primary/[0.06] px-5 py-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
              <ShieldCheck className="h-6 w-6" />
            </span>
            <div className="relative z-10">
              <div className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-primary">Secure Admin Access</div>
              <div className="mt-0.5 text-sm font-bold text-foreground">Multi-Layer Authentication</div>
              <div className="mt-0.5 text-xs leading-5 text-muted-foreground">
                Enterprise-grade authentication with secure administrator verification.
              </div>
            </div>
            <span
              aria-hidden
              className="pointer-events-none absolute right-4 top-1/2 hidden h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-primary xl:flex"
            >
              <Lock className="h-5 w-5" />
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default function AdminLoginPage() {
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => Boolean(localStorage.getItem(REMEMBER_KEY)));
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { org, slug: orgSlug } = useOrgDoor();
  // Correct credentials at the wrong organization's admin door.
  const [wrongDoor, setWrongDoor] = useState<{ message: string; url: string } | null>(null);
  const { adminLogin } = useAuth();
  const captcha = useCaptcha();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      await adminLogin(email, password, captcha.payload());
      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      // The page a guard bounced them off (?next=), else the console home.
      // Stay inside the organization whose door this is, so the console URL
      // says which organization the admin is working in.
      navigate(
        safeRedirectPath(searchParams.get("next")) ??
          (orgSlug ? `/${orgSlug}/admin/home` : "/admin/home"),
      );
    } catch (error: unknown) {
      // Challenges are single-use — hand back a fresh one on any failure.
      captcha.refresh();
      if (error instanceof WrongOrgDoorError) {
        // Keep ?next= across the door switch: an emailed link (e.g. "Review & sign
        // the NDA") opened at the wrong door must still land on its page after
        // sign-in, not on the dashboard (TKT-00159, an ECFA buyer).
        const next = searchParams.get("next");
        setWrongDoor({
          message: error.message,
          url: next ? `${error.signInUrl}?next=${encodeURIComponent(next)}` : error.signInUrl,
        });
      } else {
        toast.error(error instanceof Error ? error.message : "Authentication failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot-password (email OTP → new password), same flow as the buyer
  //    sign-in page. The /auth/forgot-password endpoints are role-agnostic.
  type FpStep = "idle" | "enter-email" | "enter-otp" | "enter-password" | "done";
  const [fpStep, setFpStep] = useState<FpStep>("idle");
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPassword, setFpNewPassword] = useState("");
  const [fpConfirmPassword, setFpConfirmPassword] = useState("");
  const [fpShowPassword, setFpShowPassword] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState("");
  const [fpCooldown, setFpCooldown] = useState(0);
  const fpCooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startFpCooldown = () => {
    setFpCooldown(60);
    if (fpCooldownRef.current) clearInterval(fpCooldownRef.current);
    fpCooldownRef.current = setInterval(() => {
      setFpCooldown((prev) => {
        if (prev <= 1) { clearInterval(fpCooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleForgotPassword = () => {
    setFpEmail(email.trim());
    setFpStep("enter-email");
    setFpError("");
  };

  const handleFpSendOtp = async () => {
    const target = fpEmail.trim();
    if (!target) return;
    setFpLoading(true);
    setFpError("");
    try {
      await api.post("/auth/forgot-password/send-otp", { email: target });
      setFpStep("enter-otp");
      setFpOtp("");
      startFpCooldown();
    } catch (err: unknown) {
      setFpError(err instanceof Error ? err.message : "Failed to send code");
    } finally {
      setFpLoading(false);
    }
  };

  const handleFpReset = async (event: React.FormEvent) => {
    event.preventDefault();
    const fpIssue = firstPasswordIssue(fpNewPassword);
    if (fpIssue) { setFpError(fpIssue); return; }
    if (fpNewPassword !== fpConfirmPassword) { setFpError("Passwords do not match"); return; }
    setFpLoading(true);
    setFpError("");
    try {
      await api.post("/auth/forgot-password/reset", {
        email: fpEmail.trim(),
        otp_code: fpOtp.trim(),
        new_password: fpNewPassword,
      });
      setFpStep("done");
    } catch (err: unknown) {
      setFpError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setFpLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setFpStep("idle");
    setFpEmail("");
    setFpOtp("");
    setFpNewPassword("");
    setFpConfirmPassword("");
    setFpError("");
    setFpCooldown(0);
    if (fpCooldownRef.current) clearInterval(fpCooldownRef.current);
  };

  // Admin sign-in always renders in the Dark · Gold theme — no light option.
  return (
    <div data-theme="dark" className="public-stage min-h-screen w-full lg:grid lg:grid-cols-[1.35fr_1fr]">
      <BrandAside org={org} />

      <div className="flex min-h-screen flex-col justify-center px-5 py-10 sm:px-8 lg:px-14 xl:px-20">
        <div className="mx-auto w-full max-w-[400px]">
          <MobileBrandHeader org={org} />
          <div className="mt-8 lg:mt-0">
            <div className="flex items-center justify-between gap-3 border-b border-border/60">
              <span className="relative -mb-px flex items-center gap-2 pb-3 text-sm font-semibold text-primary">
                <ShieldCheck className="h-4 w-4" />
                Admin Sign In
                <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />
              </span>
              <span className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-primary">
                <Lock className="h-3.5 w-3.5" />
                Restricted
              </span>
            </div>

            <div className="mt-6">
              <h2 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Welcome back, <span className="text-primary">Admin</span>
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Sign in to access your secure BusinessLocating administration portal.
              </p>
            </div>

            {wrongDoor && (
              <div className="mt-6 rounded-xl border border-primary/30 bg-primary/10 p-4">
                <p className="text-sm leading-6 text-foreground">{wrongDoor.message}</p>
                <Link
                  to={wrongDoor.url}
                  onClick={() => setWrongDoor(null)}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-gold-gradient px-4 py-2 text-sm font-semibold text-primary-foreground"
                >
                  Go to the right sign-in page
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="pl-10"
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="pl-10 pr-11"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border text-primary"
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm font-medium text-primary underline-offset-2 hover:underline"
                >
                  Forgot password?
                </button>
              </div>

              <CaptchaField captcha={captcha} />

              <Button type="submit" size="lg" className="w-full" disabled={loading || captcha.incomplete}>
                {loading ? "Authenticating..." : "Access Dashboard"}
                {!loading && <ArrowRight className="h-4 w-4" />}
              </Button>
            </form>

            <div className="mt-6 flex items-start gap-2.5 border-t border-border/50 pt-4 text-xs leading-5 text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
              <span>
                Access is restricted to authorized administrators only. Protected by enterprise-grade authentication and role-based access control.
              </span>
            </div>

            <div className="mt-4 text-center">
              <Link to={orgSlug ? `/${orgSlug}` : "/"} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                Return to marketplace
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Forgot-password modal (email OTP → new password) ── */}
      {fpStep !== "idle" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl border border-border/80 bg-card p-6 shadow-xl space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">Reset your admin password</h3>
              <button type="button" onClick={closeForgotPassword} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            {fpStep === "enter-email" && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Enter your admin account email and we'll send a 6-digit reset code.</p>
                <div className="space-y-1.5">
                  <Label htmlFor="fpEmail">Email address</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                    <Input
                      id="fpEmail"
                      type="email"
                      placeholder="admin@company.com"
                      value={fpEmail}
                      onChange={(e) => { setFpEmail(e.target.value); setFpError(""); }}
                      className="pl-10"
                      autoFocus
                    />
                  </div>
                </div>
                {fpError && <p className="text-xs font-medium text-destructive">{fpError}</p>}
                <Button type="button" className="w-full rounded-full" onClick={handleFpSendOtp} disabled={fpLoading || !fpEmail.trim()}>
                  <Mail className="h-4 w-4" />
                  {fpLoading ? "Sending..." : "Send reset code"}
                </Button>
              </div>
            )}

            {fpStep === "enter-otp" && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  A 6-digit code was sent to <strong>{fpEmail}</strong>. Enter it below.
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="fpOtp">6-digit code</Label>
                  <Input
                    id="fpOtp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={fpOtp}
                    onChange={(e) => { setFpOtp(e.target.value.replace(/\D/g, "")); setFpError(""); }}
                    className="text-center text-lg font-mono tracking-widest"
                    autoFocus
                  />
                </div>
                {fpError && <p className="text-xs font-medium text-destructive">{fpError}</p>}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    className="flex-1 rounded-full"
                    onClick={() => {
                      if (fpOtp.length !== 6) { setFpError("Enter the 6-digit code"); return; }
                      setFpError("");
                      setFpStep("enter-password");
                    }}
                    disabled={fpOtp.length !== 6}
                  >
                    Next
                  </Button>
                  <Button type="button" variant="outline" className="rounded-full" onClick={handleFpSendOtp} disabled={fpLoading || fpCooldown > 0}>
                    {fpCooldown > 0 ? `Resend in ${fpCooldown}s` : "Resend"}
                  </Button>
                </div>
              </div>
            )}

            {fpStep === "enter-password" && (
              <form onSubmit={handleFpReset} className="space-y-4">
                <p className="text-sm text-muted-foreground">Set your new password. It must meet all the requirements below.</p>
                <div className="space-y-1.5">
                  <Label htmlFor="fpNewPw">New password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                    <Input
                      id="fpNewPw"
                      type={fpShowPassword ? "text" : "password"}
                      value={fpNewPassword}
                      onChange={(e) => { setFpNewPassword(e.target.value); setFpError(""); }}
                      className="pl-10 pr-10"
                      required
                      minLength={PASSWORD_MIN_LENGTH}
                      autoFocus
                    />
                    <button type="button" onClick={() => setFpShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {fpShowPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <PasswordRequirements password={fpNewPassword} className="pt-1" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fpConfirmPw">Confirm new password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                    <Input
                      id="fpConfirmPw"
                      type="password"
                      value={fpConfirmPassword}
                      onChange={(e) => { setFpConfirmPassword(e.target.value); setFpError(""); }}
                      className="pl-10"
                      required
                      minLength={PASSWORD_MIN_LENGTH}
                    />
                  </div>
                  {fpConfirmPassword && fpNewPassword !== fpConfirmPassword && (
                    <p className="text-xs font-medium text-destructive">Passwords do not match.</p>
                  )}
                </div>
                {fpError && <p className="text-xs font-medium text-destructive">{fpError}</p>}
                <Button type="submit" className="w-full rounded-full" disabled={fpLoading}>
                  <Lock className="h-4 w-4" />
                  {fpLoading ? "Resetting..." : "Reset password"}
                </Button>
              </form>
            )}

            {fpStep === "done" && (
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
                  <CheckCircle2 className="h-7 w-7 text-emerald-500" />
                </div>
                <p className="text-sm font-semibold text-foreground">Password reset successfully!</p>
                <p className="text-xs text-muted-foreground">You can now sign in with your new password.</p>
                <Button type="button" className="w-full rounded-full" onClick={closeForgotPassword}>
                  Back to Admin Sign In
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
